import React from 'react';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import { CText } from '../../components/common';
import { CompanyInsights } from '../../api/copilot';

// ── Types ───────────────────────────────────────────────────────────────────

interface GovernanceData {
  governance_score: number | null;
  score_label: string;
  score_components: Record<string, number | null>;
  red_flags: string[];
  green_flags: string[];
  promoter_holding_trend: Array<{ period: string; percentage: number }>;
  institutional_holding_trend: Array<{ period: string; fii: number; dii: number }>;
  debt_to_equity: number | null;
  interest_coverage: number | null;
}

interface GovernanceTabProps {
  governance?: GovernanceData;
  companyInsights?: CompanyInsights;
  colors: any;
}

// ── Score Gauge (SVG semi-circle) ───────────────────────────────────────────

const ScoreGauge: React.FC<{ score: number; label: string; colors: any }> = ({ score, label, colors }) => {
  const radius = 80;
  const cx = 100;
  const cy = 95;
  const circumference = Math.PI * radius; // half-circle
  const offset = circumference - (score / 100) * circumference;

  const getScoreColor = (s: number) => {
    if (s >= 70) return '#22c55e';
    if (s >= 40) return '#f59e0b';
    return '#ef4444';
  };
  const scoreColor = getScoreColor(score);

  return (
    <div className="flex flex-col items-center">
      <svg width="200" height="120" viewBox="0 0 200 120">
        {/* Background arc */}
        <path
          d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
          fill="none"
          stroke={colors.borderColor}
          strokeWidth="12"
          strokeLinecap="round"
        />
        {/* Score arc */}
        <path
          d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
          fill="none"
          stroke={scoreColor}
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s ease-out' }}
        />
        {/* Score text */}
        <text x={cx} y={cy - 15} textAnchor="middle" fill={colors.textColor} fontSize="32" fontWeight="bold">
          {score}
        </text>
        <text x={cx} y={cy + 5} textAnchor="middle" fill={scoreColor} fontSize="14" fontWeight="600">
          {label}
        </text>
      </svg>
      <CText type="r12" color={colors.grayScale400} style={{ marginTop: -4 }}>
        Governance Score (0-100)
      </CText>
    </div>
  );
};

// ── Component Progress Bar ──────────────────────────────────────────────────

const COMPONENT_LABELS: Record<string, string> = {
  promoter_stability: 'Promoter Stability',
  leverage_health: 'Leverage Health',
  institutional_confidence: 'Institutional Confidence',
  management_stability: 'Management Stability',
  rpt_risk: 'RPT Risk',
  cons_governance: 'Governance Scan',
};

const ComponentBar: React.FC<{ name: string; score: number; colors: any }> = ({ name, score, colors }) => {
  const barColor = score >= 70 ? '#22c55e' : score >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <div className="mb-3">
      <div className="flex justify-between mb-1">
        <CText type="r12" color={colors.textColor}>{COMPONENT_LABELS[name] || name}</CText>
        <CText type="M12" color={barColor}>{score}</CText>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.borderColor }}>
        <div
          className="h-full rounded-full"
          style={{
            width: `${score}%`,
            backgroundColor: barColor,
            transition: 'width 0.6s ease-out',
          }}
        />
      </div>
    </div>
  );
};

// ── Main GovernanceTab ──────────────────────────────────────────────────────

const GovernanceTab: React.FC<GovernanceTabProps> = ({ governance, companyInsights, colors }) => {
  if (!governance || governance.governance_score === null) {
    return (
      <div className="text-center py-8">
        <CText type="M14" color={colors.grayScale500}>
          Governance data not available for this company
        </CText>
      </div>
    );
  }

  const {
    governance_score,
    score_label,
    score_components,
    red_flags,
    green_flags,
    promoter_holding_trend,
    institutional_holding_trend,
    debt_to_equity,
    interest_coverage,
  } = governance;

  const rptText = companyInsights?.related_party_transactions;

  return (
    <div>
      {/* ── Score Gauge ── */}
      <div className="flex justify-center mb-6">
        <ScoreGauge score={governance_score} label={score_label} colors={colors} />
      </div>

      {/* ── Component Breakdown ── */}
      {Object.keys(score_components).length > 0 && (
        <div
          className="p-4 rounded-xl mb-4"
          style={{ backgroundColor: colors.backgroundColor, border: `1px solid ${colors.borderColor}` }}
        >
          <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>Score Breakdown</CText>
          {Object.entries(score_components).map(([key, val]) =>
            val !== null ? <ComponentBar key={key} name={key} score={val} colors={colors} /> : null
          )}
        </div>
      )}

      {/* ── Red Flags / Green Flags ── */}
      {(red_flags.length > 0 || green_flags.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {red_flags.length > 0 && (
            <div className="p-3 rounded-xl" style={{ backgroundColor: '#ef444408', border: '1px solid #ef444430' }}>
              <div className="flex items-center gap-2 mb-2">
                {React.createElement(FiAlertTriangle as any, { size: 14, color: '#ef4444' })}
                <CText type="M14" color="#ef4444">Red Flags</CText>
              </div>
              <ul className="space-y-1.5">
                {red_flags.map((flag, i) => (
                  <li key={i} className="flex gap-2">
                    <span style={{ color: '#ef4444', flexShrink: 0 }}>-</span>
                    <CText type="r12" color={colors.textColor} style={{ lineHeight: 1.5 }}>{flag}</CText>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {green_flags.length > 0 && (
            <div className="p-3 rounded-xl" style={{ backgroundColor: '#22c55e08', border: '1px solid #22c55e30' }}>
              <div className="flex items-center gap-2 mb-2">
                {React.createElement(FiCheckCircle as any, { size: 14, color: '#22c55e' })}
                <CText type="M14" color="#22c55e">Green Flags</CText>
              </div>
              <ul className="space-y-1.5">
                {green_flags.map((flag, i) => (
                  <li key={i} className="flex gap-2">
                    <span style={{ color: '#22c55e', flexShrink: 0 }}>+</span>
                    <CText type="r12" color={colors.textColor} style={{ lineHeight: 1.5 }}>{flag}</CText>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Promoter Holding Trend ── */}
      {promoter_holding_trend.length > 1 && (
        <div
          className="p-4 rounded-xl mb-4"
          style={{ backgroundColor: colors.backgroundColor, border: `1px solid ${colors.borderColor}` }}
        >
          <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>Promoter Holding Trend</CText>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={promoter_holding_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.borderColor} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 10, fill: colors.grayScale400 }}
                axisLine={{ stroke: colors.borderColor }}
              />
              <YAxis
                domain={['auto', 'auto']}
                tick={{ fontSize: 10, fill: colors.grayScale400 }}
                axisLine={{ stroke: colors.borderColor }}
                tickFormatter={(v: number) => `${v}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.borderColor}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value: number) => [`${value}%`, 'Promoter Holding']}
              />
              <Line
                type="monotone"
                dataKey="percentage"
                stroke="#6B39F4"
                strokeWidth={2}
                dot={{ fill: '#6B39F4', r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Institutional Holding Trend ── */}
      {institutional_holding_trend.length > 1 && (
        <div
          className="p-4 rounded-xl mb-4"
          style={{ backgroundColor: colors.backgroundColor, border: `1px solid ${colors.borderColor}` }}
        >
          <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>Institutional Holding Trend</CText>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={institutional_holding_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.borderColor} />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 10, fill: colors.grayScale400 }}
                axisLine={{ stroke: colors.borderColor }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: colors.grayScale400 }}
                axisLine={{ stroke: colors.borderColor }}
                tickFormatter={(v: number) => `${v}%`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: colors.inputBackground,
                  border: `1px solid ${colors.borderColor}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value: number, name: string) => [
                  `${value}%`,
                  name === 'fii' ? 'FII' : 'DII',
                ]}
              />
              <Area
                type="monotone"
                dataKey="dii"
                stackId="1"
                stroke="#22c55e"
                fill="#22c55e"
                fillOpacity={0.3}
              />
              <Area
                type="monotone"
                dataKey="fii"
                stackId="1"
                stroke="#6B39F4"
                fill="#6B39F4"
                fillOpacity={0.3}
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 mt-2">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#6B39F4' }} />
              <CText type="r10" color={colors.grayScale400}>FII</CText>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#22c55e' }} />
              <CText type="r10" color={colors.grayScale400}>DII</CText>
            </div>
          </div>
        </div>
      )}

      {/* ── Debt Metrics Card ── */}
      {(debt_to_equity !== null || interest_coverage !== null) && (
        <div
          className="p-4 rounded-xl mb-4"
          style={{ backgroundColor: colors.backgroundColor, border: `1px solid ${colors.borderColor}` }}
        >
          <CText type="M14" color={colors.primary} style={{ marginBottom: 12 }}>Debt Metrics</CText>
          <div className="grid grid-cols-2 gap-4">
            {debt_to_equity !== null && (
              <div>
                <CText type="r12" color={colors.grayScale400}>Debt-to-Equity</CText>
                <CText
                  type="B20"
                  color={debt_to_equity > 1 ? '#ef4444' : debt_to_equity > 0.5 ? '#f59e0b' : '#22c55e'}
                >
                  {debt_to_equity.toFixed(2)}x
                </CText>
              </div>
            )}
            {interest_coverage !== null && (
              <div>
                <CText type="r12" color={colors.grayScale400}>Interest Coverage</CText>
                <CText
                  type="B20"
                  color={interest_coverage < 2 ? '#ef4444' : interest_coverage < 4 ? '#f59e0b' : '#22c55e'}
                >
                  {interest_coverage.toFixed(1)}x
                </CText>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── RPT Summary ── */}
      {rptText && (
        <div
          className="p-4 rounded-xl mb-4"
          style={{ backgroundColor: colors.backgroundColor, border: `1px solid ${colors.borderColor}` }}
        >
          <CText type="M14" color={colors.primary} style={{ marginBottom: 8 }}>Related Party Transactions</CText>
          <CText type="r14" color={colors.textColor} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {rptText}
          </CText>
        </div>
      )}
    </div>
  );
};

export default GovernanceTab;
